import React, { useEffect, useState } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const GameUserList = () => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState({
    wins: 0,
    losses: 0,
    totalGames: 0,
  });

  useEffect(() => {
    setLoading(true);
    const fetchMatches = async () => {
      try {
        const response = await api.get("/match");
        const statsRes = await api.get("/match/stats");

        setMatches(Array.isArray(response.data) ? response.data : []);
        setStats(statsRes.data);
      } catch {
        toast.error(t("gameUserList_fetchError"));
      } finally {
        setLoading(false);
      }
    };
    fetchMatches();
  }, []);

  const getWinnerLabel = (match) => {
  if (!match.winnerId) {
    if (match.player2Type === "AI") return t("gameUserList_winnerComputer");
    if (match.player2Type === "ANON") return t("gameUserList_winnerAnonymous");
    return t("gameUserList_winnerUnknown");
  }

  if (match.winnerId === match.player1Id?.id) {
    return match.player1Id.username;
  }

  if (match.winnerId === match.player2Id?.id) {
    return match.player2Id.username;
  }

  return t("gameUserList_winnerUnknown");
};

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3">
        <Loader2 size={28} className="animate-spin text-violet-400" />
        <span className="text-sm text-zinc-500">{t("gameUserList_loading")}</span>
      </div>
    );
  }

  if (!loading && matches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-2 text-center border border-white/8 rounded-xl">
        <span className="text-zinc-500 text-sm">{t("gameUserList_noMatches")}</span>
        <span className="text-zinc-600 text-xs">
          {t("gameUserList_noMatchesDescription")}
        </span>
      </div>
    );
  }

  const modeLabel = (mode) => {
    if (mode === "LOCAL")
      return {
        text: t("gameUserList_modeLocal"),
        cls: "text-sky-400 bg-sky-400/10 border-sky-400/20",
      };
    return {
      text: t("gameUserList_modeVsAI"),
      cls: "text-violet-400 bg-violet-400/10 border-violet-400/20",
    };
  };

  return (
    <>
      <div className="rounded-xl border border-white/8 bg-white/5 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-zinc-400 text-xs">{t("gameUserList_statsWins")}</div>
            <div className="text-green-400 text-lg font-bold">
              {stats.wins ?? 0}
            </div>
          </div>

          <div>
            <div className="text-zinc-400 text-xs">{t("gameUserList_statsLosses")}</div>
            <div className="text-red-400 text-lg font-bold">
              {stats.losses ?? 0}
            </div>
          </div>

          <div>
            <div className="text-zinc-400 text-xs">{t("gameUserList_statsTotal")}</div>
            <div className="text-white text-lg font-bold">
              {(stats.wins ?? 0) + (stats.losses ?? 0)}
            </div>
          </div>

          <div>
            <div className="text-zinc-400 text-xs">{t("gameUserList_statsWinRate")}</div>
            <div className="text-blue-400 text-lg font-bold">
              {stats.wins + stats.losses > 0
                ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1)
                : 0}
              %
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-white/8 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/8 hover:bg-transparent">
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                {t("gameUserList_date")}
              </TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                {t("gameUserList_tableMode")}
              </TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                {t("gameUserList_tablePlayer1")}
              </TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                {t("gameUserList_tablePlayer2")}
              </TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                {t("gameUserList_tableScore")}
              </TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                {t("gameUserList_tableWinner")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matches.map((match) => {
              const { text, cls } = modeLabel(match.mode);
              return (
                <TableRow
                  key={match.id}
                  className="border-white/5 hover:bg-white/3 transition-colors"
                >
                  <TableCell className="text-zinc-300 text-sm text-center">
                    {`${new Date(match.playedAt).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })} - ${new Date(match.playedAt).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\.\s?/g, '/')}`}
                  </TableCell>
                  <TableCell className="text-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${cls}`}
                    >
                      {text}
                    </span>
                  </TableCell>
                  <TableCell className="text-zinc-300 text-sm text-center">
                    {match.player1Id.username}
                  </TableCell>
                  <TableCell className="text-zinc-300 text-sm text-center">
                    {match.player2Type === "AI"
                      ? t("gameUserList_winnerComputer")
                      : match.player2Type === "ANON"
                        ? t("gameUserList_winnerAnonymous")
                        : match.player2Id?.username}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-white text-center">
                    {match.player1Score} — {match.player2Score}
                  </TableCell>
                  <TableCell className="text-zinc-300 text-sm text-center">
                    {getWinnerLabel(match)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export default GameUserList;
